import React, { useState } from 'react'
import {
	Card,
	CardBody,
	VStack,
	HStack,
	Text,
	Badge,
	IconButton,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	useColorModeValue,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	Button,
	Input,
	FormControl,
	FormLabel,
	FormErrorMessage,
	Switch,
	Select,
	useDisclosure,
	Box,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { CordeCommonModelsDtoContactContact as ContactDto, CordeCommonModelsDtoEntityEntity as EntityDto } from '@/api/generated/v1/api'
import { useContacts, ContactFormData } from '@/hooks/useContacts'

// Icons
const SettingsIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
		<path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.82,11.69,4.82,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.43-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
	</svg>
)

interface ContactCardProps {
	contact: ContactDto
	entity: EntityDto
	isEditable?: boolean
	onContactUpdated?: () => void
	onContactDeleted?: () => void
}

export const ContactCard: React.FC<ContactCardProps> = ({
	contact,
	entity,
	isEditable = false,
	onContactUpdated,
	onContactDeleted,
}) => {
	const { t } = useTranslation()
	const {
		isLoading,
		deleteContact,
		verifyContact,
		sendContactVerification,
		setPrimaryContact,
		updateContact,
	} = useContacts()

	const { isOpen: isVerifyOpen, onOpen: onVerifyOpen, onClose: onVerifyClose } = useDisclosure()
	const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
	const [verificationCode, setVerificationCode] = useState('')
	const [verifyError, setVerifyError] = useState('')
	const [editFormData, setEditFormData] = useState<ContactFormData>({
		type: '',
		value: '',
		label: '',
		isPrimary: false,
	})

	const getContactTypeIcon = (type: string) => {
		switch (type?.toLowerCase()) {
			case 'email':
				return '📧'
			case 'phone':
				return '📱'
			case 'telegram':
				return '📞'
			case 'discord':
				return '💬'
			case 'instagram':
				return '📷'
			case 'twitter':
				return '🐦'
			default:
				return '📄'
		}
	}

	const getContactDisplayName = (type: string) => {
		switch (type?.toLowerCase()) {
			case 'email':
				return t('profile.email')
			case 'phone':
				return t('profile.phone')
			case 'telegram':
				return t('profile.telegram')
			case 'discord':
				return t('profile.discord')
			case 'instagram':
				return t('profile.instagram')
			case 'twitter':
				return t('profile.twitter')
			default:
				return type
		}
	}

	const handleDelete = async () => {
		if (contact.id && await deleteContact(contact.id)) {
			onContactDeleted?.()
		}
	}

	const handleSendVerification = async () => {
		if (contact.id && await sendContactVerification(contact.id)) {
			onVerifyOpen()
		}
	}

	const handleVerify = async () => {
		if (!verificationCode.trim()) {
			setVerifyError(t('contacts.error.verificationCodeRequired'))
			return
		}

		setVerifyError('')
		if (contact.id && await verifyContact(contact.id, verificationCode)) {
			setVerificationCode('')
			onVerifyClose()
			onContactUpdated?.()
		}
	}

	const handleSetPrimary = async () => {
		if (contact.id && contact.type && await setPrimaryContact(entity, contact.id, contact.type)) {
			onContactUpdated?.()
		}
	}

	const handleEdit = () => {
		setEditFormData({
			type: contact.type || '',
			value: contact.value || '',
			label: contact.metadata?.label || '',
			isPrimary: contact.is_primary || false,
		})
		onEditOpen()
	}

	const handleFormSubmit = async () => {
		if (contact.id && await updateContact(contact.id, editFormData)) {
			onEditClose()
			onContactUpdated?.()
		}
	}

	return (
		<>
			<Card variant="outline" size="sm">
				<CardBody p={4}>
					<VStack spacing={3} align="stretch">
						<HStack spacing={3}>
							<Box
								p={2}
								borderRadius="md"
								bg={useColorModeValue('gray.100', 'gray.700')}
								color={useColorModeValue('gray.600', 'gray.300')}
							>
								<Text fontSize="lg">{getContactTypeIcon(contact.type || '')}</Text>
							</Box>
							<VStack align="start" spacing={1} flex={1}>
								<Text fontSize="sm" fontWeight="semibold" color="text.default">
									{getContactDisplayName(contact.type || '')}
								</Text>
								<Text fontSize="sm" color="text.muted" wordBreak="break-all">
									{contact.value}
								</Text>
								{contact.metadata?.label && (
									<Text fontSize="xs" color="text.muted">
										{contact.metadata.label}
									</Text>
								)}
							</VStack>
							{isEditable && (
								<Menu>
									<MenuButton
										as={IconButton}
										aria-label="Contact options"
										icon={<SettingsIcon />}
										size="sm"
										variant="ghost"
										isLoading={isLoading}
									/>
									<MenuList>
										{!contact.is_verified && (
											<MenuItem onClick={handleSendVerification}>
												{t('contacts.sendVerification')}
											</MenuItem>
										)}
										{!contact.is_primary && contact.is_verified && (
											<MenuItem onClick={handleSetPrimary}>
												{t('contacts.setPrimary')}
											</MenuItem>
										)}
										<MenuItem onClick={handleEdit}>
											{t('contacts.edit')}
										</MenuItem>
										<MenuItem onClick={handleDelete} color="red.400">
											{t('contacts.delete')}
										</MenuItem>
									</MenuList>
								</Menu>
							)}
						</HStack>

						{(contact.is_verified || contact.is_primary) && (
							<HStack spacing={2} justify="flex-start">
								{contact.is_verified && (
									<Badge colorScheme="green" variant="subtle" size="sm">
										{t('profile.verified')}
									</Badge>
								)}
								{contact.is_primary && (
									<Badge colorScheme="blue" variant="subtle" size="sm">
										{t('profile.primary')}
									</Badge>
								)}
							</HStack>
						)}
					</VStack>
				</CardBody>
			</Card>

			{/* Verification Modal */}
			<Modal isOpen={isVerifyOpen} onClose={onVerifyClose}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>{t('contacts.verifyContact')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<VStack spacing={4}>
							<Text>
								{t('contacts.verificationSentTo', { value: contact.value })}
							</Text>
							<FormControl isInvalid={!!verifyError}>
								<FormLabel>{t('contacts.verificationCode')}</FormLabel>
								<Input
									value={verificationCode}
									onChange={(e) => setVerificationCode(e.target.value)}
									placeholder={t('contacts.enterVerificationCode')}
								/>
								{verifyError && (
									<FormErrorMessage>{verifyError}</FormErrorMessage>
								)}
							</FormControl>
						</VStack>
					</ModalBody>
					<ModalFooter>
						<Button variant="ghost" mr={3} onClick={onVerifyClose}>
							{t('common.cancel')}
						</Button>
						<Button
							colorScheme="blue"
							onClick={handleVerify}
							isLoading={isLoading}
						>
							{t('contacts.verify')}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Edit Contact Modal */}
			<Modal isOpen={isEditOpen} onClose={onEditClose}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>{t('contacts.editContact')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<VStack spacing={4} align="stretch">
							<FormControl>
								<FormLabel>{t('contacts.type')}</FormLabel>
								<Select
									value={editFormData.type}
									onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })}
									placeholder={t('contacts.selectType')}
								>
									<option value="email">{t('profile.email')}</option>
									<option value="phone">{t('profile.phone')}</option>
									<option value="telegram">{t('profile.telegram')}</option>
									<option value="discord">{t('profile.discord')}</option>
									<option value="instagram">{t('profile.instagram')}</option>
									<option value="twitter">{t('profile.twitter')}</option>
								</Select>
							</FormControl>
							<FormControl>
								<FormLabel>{t('contacts.value')}</FormLabel>
								<Input
									value={editFormData.value}
									onChange={(e) => setEditFormData({ ...editFormData, value: e.target.value })}
									placeholder={t('contacts.enterValue')}
								/>
							</FormControl>
							<FormControl>
								<FormLabel>{t('contacts.isVerified')}</FormLabel>
								<Switch
									isChecked={contact.is_verified}
									isDisabled={true}
								/>
							</FormControl>
							<FormControl>
								<FormLabel>{t('contacts.isPrimary')}</FormLabel>
								<Switch
									isChecked={editFormData.isPrimary}
									onChange={(e) => setEditFormData({ ...editFormData, isPrimary: e.target.checked })}
								/>
							</FormControl>
							<FormControl>
								<FormLabel>{t('contacts.label')}</FormLabel>
								<Input
									value={editFormData.label}
									onChange={(e) => setEditFormData({ ...editFormData, label: e.target.value })}
									placeholder={t('contacts.labelPlaceholder')}
								/>
							</FormControl>
						</VStack>
					</ModalBody>
					<ModalFooter>
						<Button variant="ghost" mr={3} onClick={onEditClose}>
							{t('common.cancel')}
						</Button>
						<Button
							colorScheme="blue"
							onClick={handleFormSubmit}
							isLoading={isLoading}
						>
							{t('common.save')}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	)
} 